import { IColumn, IDetailsColumnProps, IDetailsRowProps, IObjectWithKey } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPActionButton, IPLabel, IPLink, IPSearchBox, IPSpinner, IPText, IPTooltipHost } from 'baseComponents';
import { IPDetailsList, IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelection, IPSelectionMode } from 'baseComponents/Utils/Selection';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { getEntityIcon, onRenderDetailsHeaderGlobal } from 'globalFunctions';
import { globalStackTokensGapSmall, newIcon } from 'globalStyles';
import Entity, { EntityTypes } from 'models/entity';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetAsset } from 'services/Api/assetService';
import { apiGetControl } from 'services/Api/controlService';
import { apiGetKPI } from 'services/Api/kpiService';
import { apiGetObjective } from 'services/Api/objectiveService';
import { apiGetProcess } from 'services/Api/processService';
import { apiGetRisk } from 'services/Api/riskService';
import { apiGetSingleTask } from 'services/Api/taskService';
import { apiGetTheme } from 'services/Api/themeService';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';
import { sortOnEntity } from 'utils/sorting';
import InfoMessage from '../Notification/InfoMessage';

interface IEntityPickerProps {
  className?: string;
  entities: Entity[];
  isLoading?: boolean;
  addSelectedEntity: (item: Entity) => void;
  addSelectedEntities?: (items: Entity[]) => void;
  loadData?: () => void;
  isBusyAdding?: number;
  suggestionLimit?: number;
  showHeader?: boolean;
  setSearchTextToSelectedEntity?: boolean;
  selectedEntity?: Entity;
  clearSearchText?: () => void;
  expandOnFocus?: boolean;
  isOnPanel: boolean;
  placeholder?: string;
  allowMultiAdd?: boolean;
  extraColumns?: IColumn[];
  compact?: boolean;
  clearKey?: string;
  panelListHeightPercentage?: number;
  panelListMaxHeightPercentage?: number;
  panelListMinHeight?: number;
  disabledEntities?: (entity: Entity) => [boolean, string | undefined];
  onSelectionChanged?: (items: Entity[]) => void;
  hideAddSelectionButton?: boolean;
}

const EntityPicker = (props: IEntityPickerProps) => {
  const { t } = useTranslation(['translation']);
  const appContext = useContext(AppContext);
  const [filteredEntities, setFilteredEntities] = useState<Entity[]>([]);
  const [filterText, setFilterText] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isLoadingSelectedEntity, setIsLoadingSelectedEntity] = useState<boolean>(false);
  const [searchHasFocus, setSearchHasFocus] = useState<boolean>(false);
  const [selectedEntities, setSelectedEntities] = useState<Entity[]>([]);
  //the selection is created once, so it reads the latest callback through a ref
  const disabledEntitiesRef = useRef(props.disabledEntities);
  disabledEntitiesRef.current = props.disabledEntities;
  const [selection] = useState<IPSelection<Entity>>(
    new IPSelection({
      selectionMode: IPSelectionMode.multiple,
      canSelectItem: (item: Entity) => !(disabledEntitiesRef.current?.(item)[0] ?? false),
      getKey: (item: Entity) => {
        return item.id;
      },
      onSelectionChanged: () => {
        const selectedItems = selection.getSelection();
        setSelectedEntities(selectedItems);
        props.onSelectionChanged?.(selectedItems);
      },
    }),
  );

  const selectEntity = (item: Entity) => {
    if (!item) return;
    if (props.disabledEntities?.(item)[0]) return;
    //in multi-add mode a click on a row must not throw away the ticked rows: add the selection plus the clicked row
    if (props.allowMultiAdd && props.addSelectedEntities && selectedEntities.length > 0) {
      const items = selectedEntities.some((e) => e.entityId === item.entityId)
        ? selectedEntities
        : [...selectedEntities, item];
      props.addSelectedEntities(items);

      return;
    }
    props.addSelectedEntity(item);
    if (props.setSearchTextToSelectedEntity) {
      setFilterText(item.entityName);
      setFilteredEntities([]);
    }
  };

  const columns: IColumn[] = [
    {
      key: 'controls',
      name: t('translation:General.Suggestions.Header'),
      minWidth: 100,
      isMultiline: true,
      isResizable: true,
      onRenderHeader: !props.allowMultiAdd
        ? undefined
        : (
            colProps?: IDetailsColumnProps | undefined,
            defaultRender?: ((props?: IDetailsColumnProps | undefined) => JSX.Element | null) | undefined,
          ): JSX.Element | null => {
            return (
              <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
                <IPLabel>{colProps?.column.name}</IPLabel>
                {!props.hideAddSelectionButton && (
                  <IPActionButton
                    iconProps={newIcon}
                    text={t('translation:General.Suggestions.AddSelection')}
                    onClick={() => {
                      if (props.addSelectedEntities) {
                        props.addSelectedEntities(selectedEntities);
                      }
                    }}
                  />
                )}
              </IPStack>
            );
          },
      onRender: (item: Entity, index: number | undefined) => {
        const isDisabled = props.disabledEntities ? props.disabledEntities(item)[0] : false;

        return (
          <IPStack
            horizontal
            tokens={globalStackTokensGapSmall}
            verticalAlign="center"
            styles={props.isBusyAdding ? {} : { root: { cursor: 'pointer' } }}
          >
            {props.isBusyAdding === item.entityId && <IPSpinner />}
            {props.isBusyAdding !== item.entityId && <IPStack.Item>{getEntityIcon(item)}</IPStack.Item>}
            <IPStack.Item>
              <IPText>
                <IPLink onClick={() => selectEntity(item)} disabled={isDisabled}>
                  {item.entityName}
                </IPLink>
              </IPText>
            </IPStack.Item>
          </IPStack>
        );
      },
    },
    //Allow the caller to add extra columns to the picker
    ...(props.extraColumns ?? []),
  ];

  //greys out rows that may not be picked (e.g. already linked) and explains why in a tooltip
  const onRenderRow = (
    rowProps?: IDetailsRowProps,
    defaultRender?: (props?: IDetailsRowProps) => JSX.Element | null,
  ): JSX.Element | null => {
    if (!defaultRender) return null;
    const [isDisabled, disabledMessage] = rowProps?.item
      ? (props.disabledEntities?.(rowProps.item as Entity) ?? [false, undefined])
      : [false, undefined];

    if (rowProps && isDisabled) {
      rowProps.disabled = true;

      return (
        <IPTooltipHost content={disabledMessage} directionalHint={IPDirectionalHint.topLeftEdge}>
          {defaultRender(rowProps)}
        </IPTooltipHost>
      );
    }

    return defaultRender(rowProps);
  };

  const onFilter = (event?: React.ChangeEvent<HTMLInputElement>, filterText?: string): void => {
    //lazy load data when a callback is specified
    if (!isLoaded && !props.isLoading && props.loadData && props.entities.length === 0) {
      setIsLoaded(true);
      props.loadData();
    }
    setFilterText(filterText ? filterText : '');
    if (!filterText && props.clearSearchText) {
      props.clearSearchText();
    }
  };

  useEffect(() => {
    const loadEntity = async (source: Entity | undefined) => {
      if (!source || isLoadingSelectedEntity) return;
      setIsLoadingSelectedEntity(true);

      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        switch (props.selectedEntity?.typeOfEntity) {
          case EntityTypes.Control:
            const control = await apiGetControl(source.entityId, false, false, accessToken, appContext.globalDataCache);
            if (control) setFilterText(control.code + ' ' + control.name);
            break;
          case EntityTypes.Requirement:
            const theme = await apiGetTheme(source.entityId, false, false, accessToken, appContext.globalDataCache);
            if (theme) setFilterText(theme.code + ' ' + theme.name);
            break;
          case EntityTypes.Process:
            const process = await apiGetProcess(source.entityId, accessToken, appContext.globalDataCache);
            if (process) setFilterText(process.code + ' ' + process.name);
            break;
          case EntityTypes.Task:
            const taskCol = await apiGetSingleTask(source.entityId, true, accessToken, appContext.globalDataCache);
            if (taskCol && taskCol.tasks.length > 0) setFilterText(taskCol.tasks[0].name);
            break;
          case EntityTypes.Objective:
            const objective = await apiGetObjective(source.entityId, accessToken, appContext.globalDataCache);
            if (objective) setFilterText(objective.code + ' ' + objective.name);
            break;
          case EntityTypes.Asset:
            const asset = await apiGetAsset(source.entityId, accessToken, appContext.globalDataCache);
            if (asset) setFilterText(asset.code + ' ' + asset.name);
            break;
          case EntityTypes.Risk:
            const riskCol = await apiGetRisk(source.entityId, accessToken, appContext);
            if (riskCol && riskCol.risks && riskCol.risks.length > 0) {
              const risk = riskCol.risks[0];
              setFilterText(risk.code + ' ' + risk.name);
            }
            break;
          case EntityTypes.KPI:
            const kpi = await apiGetKPI(source.entityId, accessToken, undefined);
            if (kpi) {
              setFilterText(kpi.name);
            }
            break;
          default:
            throw new AppError(`Entitytype not implemented: ${props.selectedEntity?.typeOfEntity}`);
        }
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoadingSelectedEntity(false);
      }
    };

    if (props.selectedEntity) {
      let entity: Entity | undefined = undefined;
      if (isLoaded) {
        entity = props.entities.find((e) => e.entityId === props.selectedEntity?.entityId);
        setFilterText(entity?.entityName || '');
      } else {
        if (!isLoaded && !props.isLoading && props.loadData && props.entities.length === 0) {
          setIsLoaded(true);
          props.loadData();
        }
        loadEntity(props.selectedEntity);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedEntity]);

  useEffect(() => {
    if (props.clearKey) {
      setFilterText('');
    }
  }, [props.clearKey]);

  useEffect(() => {
    if (!filterText) {
      setFilteredEntities([]);

      return;
    }
    if (props.selectedEntity && props.setSearchTextToSelectedEntity === true) {
      //hide search results when an entity is selected
      return;
    } else {
      const filterLower = filterText.toLowerCase();
      setFilteredEntities(
        props.entities
          .filter((entity) => {
            return (
              entity.entityName.toLowerCase().includes(filterLower) ||
              entity.entityCode?.toLowerCase().includes(filterLower) ||
              entity.entityId.toString().includes(filterLower)
            );
          })
          .sort((a, b) => {
            return sortOnEntity(a, b);
          })
          .slice(0, props.suggestionLimit ?? (props.allowMultiAdd ? 1000 : 50)),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterText, props.entities]);

  const getHeight = (): number => {
    let height = 37;
    if (filteredEntities.length > 0 || (props.expandOnFocus && searchHasFocus) || isMessageBarVisible()) {
      if (props.panelListMinHeight) {
        height += props.panelListMinHeight + 38;
      } else {
        height += 253;
        if (props.compact) height -= 87;
      }
    }

    return height;
  };

  const isMessageBarVisible = (): boolean => {
    return (
      filterText.length > 0 &&
      filteredEntities.length === 0 &&
      !props.isLoading &&
      !(props.selectedEntity && props.setSearchTextToSelectedEntity === true)
    );
  };

  //
  // Main render
  //
  return (
    <IPStack
      styles={{
        root: {
          overflow: 'hidden',
          height: getHeight(),
          transition: 'height 0.3s ease',
        },
      }}
    >
      <IPStack.Item>
        <IPStack horizontal tokens={globalStackTokensGapSmall}>
          <IPStack.Item grow>
            <IPSearchBox
              className={props.className}
              value={filterText}
              underlined={false}
              placeholder={props.placeholder ?? t('translation:General.Search.Placeholder')}
              onChange={onFilter}
              onFocus={() => setSearchHasFocus(true)}
              onBlur={() => setSearchHasFocus(false)}
            />
          </IPStack.Item>
          {props.isLoading && (
            <IPStack.Item>
              <IPSpinner size={IPSpinnerSize.large} />
            </IPStack.Item>
          )}
        </IPStack>
      </IPStack.Item>
      {isMessageBarVisible() && (
        <IPStack.Item styles={{ root: { paddingTop: 5 } }}>
          <InfoMessage message={t('translation:General.Search.NoData')} onDismiss={undefined}></InfoMessage>
        </IPStack.Item>
      )}
      {(filteredEntities.length > 0 || (props.expandOnFocus && searchHasFocus)) && (
        <ScrollableStackItem
          isOnPanel={props.isOnPanel}
          isScrollParent={true}
          height={props.panelListHeightPercentage ?? 20}
          maxHeight={props.panelListMaxHeightPercentage ?? 40}
          minHeight={props.panelListMinHeight ?? (props.compact ? 165 : 215)}
        >
          {filteredEntities.length > 0 && (
            <IPDetailsList
              items={filteredEntities}
              columns={columns}
              compact={true}
              //a stable set key keeps the ticked rows when the item array is recreated by a parent render
              setKey="entityPicker"
              selection={selection as IPSelection<IObjectWithKey>}
              selectionMode={props.allowMultiAdd ? IPSelectionMode.multiple : IPSelectionMode.none}
              layoutMode={IPDetailsListLayoutMode.justified}
              selectionPreservedOnEmptyClick={false}
              isHeaderVisible={!appContext.isMobileView && props.showHeader !== false && filterText.length > 0}
              styles={{ root: { marginTop: 0, transition: 'all 0.3s ease' } }}
              onRenderDetailsHeader={props.allowMultiAdd ? onRenderDetailsHeaderGlobal : undefined}
              onRenderRow={onRenderRow}
            />
          )}
        </ScrollableStackItem>
      )}
    </IPStack>
  );
};

export default EntityPicker;
